<template>
    <div>
        <div class="d-flex justify-content-between align-items-end border-bottom">
            <div class="d-flex align-items-center">
                <a href="javascript:void(0)" class="mr-2" role="button" @click="collapsed = !collapsed">
                    <i :class="['fas text-muted', collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                </a>       
                <h2>{{ claim.patient_name }}</h2>
            </div>            
            <claim-share-button class="mb-1" :shared="claim.is_shared" :disabled="claim.disable_sharing" @click="toggleSharing"></claim-share-button>
        </div>
        <div v-show="!collapsed" class="container-fluid mt-2">
            <div class="row">
                <div class="col">
                    <fieldset class="border rounded">
                        <legend class="w-auto legend-sm mx-2 px-2">Patient</legend>
                        <div class="d-flex px-3 pb-3">
                            <div class="flex-column font-weight-bold pr-3">
                                <div>Name:</div>
                                <div>Social Security Number:</div>
                                <div>Date of Birth:</div>
                                <div>Date of Injury:</div>
                                <div>Parts of Body Injured:</div>
                            </div>                                                                                            
                            <div class="flex-column">
                                <div>{{ claim.patient_full_name }}</div>
                                <div>
                                    <ssn-input v-if="edit['patient_ssn']" id="patient_ssn" class="form-control form-control-sm" v-model="claim.patient_ssn" @blur.native="change($event.target.id)" @keyup.enter.native="change($event.target.id)" v-focus></ssn-input>
                                    <span v-else @click="edit['patient_ssn'] = true">                                
                                        <span v-if="claim.patient_ssn">{{ claim.patient_ssn }}</span>
                                        <span v-else class="font-italic text-muted">Missing social security number</span>
                                        <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                    </span>                                    
                                </div>
                                <div>{{ claim.patient_dob }}</div>
                                <div>
                                    <input v-if="edit['injury_date']" id="injury_date" type="date" class="form-control form-control-sm" v-model="claim.injury_date" @blur="change($event.target.id)" @keyup.enter="change($event.target.id)" v-focus>
                                    <span v-else @click="edit['injury_date'] = true">                 
                                        <span v-if="claim.injury_date">{{ claim.injury_date }}</span>
                                        <span v-else class="font-italic text-muted">Missing date of injury</span>
                                        <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                    </span>                                    
                                </div>
                                <div>
                                    <input v-if="edit['injured_body_parts']" id="injured_body_parts" class="form-control form-control-sm" v-model="claim.injured_body_parts" @blur="change($event.target.id)" @keyup.enter="change($event.target.id)" v-focus>
                                    <span v-else @click="edit['injured_body_parts'] = true">                                
                                        <span v-if="claim.injured_body_parts">{{ claim.injured_body_parts }}</span>
                                        <span v-else class="font-italic text-muted">Missing parts of body injured</span>
                                        <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                    </span>                                    
                                </div>                                
                            </div>
                            <div class="flex-column flex-grow-1"></div>                                                                             
                        </div>                        
                    </fieldset>
                </div>
                <div class="col">
                    <fieldset class="border rounded">
                        <legend class="w-auto legend-sm mx-2 px-2">Provider</legend>
                        <div class="d-flex px-3 pb-3">
                            <div class="flex-column font-weight-bold pr-3">
                                <div>Client:</div>                                
                                <div><small><span class="font-weight-bold">NPI:</span> {{ claim.client_npi }}</small></div>
                                <div>&nbsp;</div>                                                                                                
                                <div>Rendering Provider:</div>
                                <div><small><span class="font-weight-bold">NPI:</span> {{ claim.rendering_provider_npi }}</small></div>
                            </div>
                            <div class="flex-column">
                                <div>{{ claim.client_name }}</div>
                                <div><small><span class="font-weight-bold">EIN:</span> {{ claim.client_ein }}</small></div>
                                <div>&nbsp;</div>                                
                                <div>{{ claim.rendering_provider_name }}</div>                          
                                <div>&nbsp;</div>                                                                                               
                            </div>
                            <div class="flex-column flex-grow-1"></div>
                        </div>
                    </fieldset>                        
                </div>              
            </div>
            <div class="row">
                <div class="col">
                    <fieldset class="border rounded">
                        <legend class="w-auto legend-sm mx-2 px-2">Employer</legend>
                        <div class="d-flex px-3 pb-3">
                            <div class="flex-column font-weight-bold pr-3">
                                <div>Name:</div>
                                <div>Address:</div>
                            </div>
                            <div class="flex-column">
                                <div>
                                    <input v-if="edit['employer_name']" id="employer_name" class="form-control form-control-sm" v-model="claim.employer_name" @blur="change($event.target.id)" @keyup.enter="change($event.target.id)" v-focus>
                                    <span v-else @click="edit['employer_name'] = true">
                                        <span v-if="claim.employer_name">{{ claim.employer_name }}</span>
                                        <span v-else class="font-italic text-muted">Missing employer name</span>
                                        <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                    </span>                                    
                                </div>
                                <div>{{ employerAddress }}</div>                                                                                                                                                    
                            </div>
                            <div class="flex-column flex-grow-1"></div>                            
                        </div>
                    </fieldset>
                </div>                  
                <div class="col">
                    <fieldset class="border rounded">
                        <legend class="w-auto legend-sm mx-2 px-2">Payer</legend>
                        <div class="d-flex px-3 pb-3">
                            <div class="flex-column font-weight-bold pr-3">
                                <div>Name:</div>
                                <div>Claim Number:</div>
                            </div>
                            <div class="flex-column">
                                <div>{{ claim.payer_name }}</div>
                                <div>
                                    <input v-if="edit['claim_number']" id="claim_number" class="form-control form-control-sm" v-model="claim.claim_number" @blur="change($event.target.id)" @keyup.enter="change($event.target.id)" v-focus>
                                    <span v-else @click="edit['claim_number'] = true">
                                        <span v-if="claim.claim_number">{{ claim.claim_number }}</span>
                                        <span v-else class="font-italic text-muted">Missing claim number</span>
                                        <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                    </span>                                    
                                </div>                                                               
                            </div>
                            <div class="flex-column flex-grow-1"></div>
                        </div>
                    </fieldset>
                </div>                
            </div>            
        </div>
    </div>  
</template>

<script>
import ClaimShareButton from '../buttons/ClaimShareButtonComponent.vue';

export default {
    components: {
        ClaimShareButton
    },
    props: {
        dataClaim: Object,
        state: Number
    },
    data() {
        return {
            collapsed: false,
            claim: this.dataClaim,
            edit: []
        }
    },    
    computed: {
        employerAddress: function () {

            let retval = '';
            if (this.claim.employer_address) { retval += this.claim.employer_address + ' '; }
            if (this.claim.employer_city) { retval += this.claim.employer_city + ', '; }
            if (this.claim.employer_state) { retval += this.claim.employer_state + ' '; }
            if (this.claim.employer_postal_code) { retval += this.claim.employer_postal_code; }

            return retval.trim();
        }
    },
    methods: {
        change: function (key) {

            this.edit[key] = false;

            axios.patch(`/ajax/researcher/claim/${this.claim.id}`, {
                key: key,
                value: this.claim[key]
            });
        },
        toggleSharing: function (shared) {

            let url = `/ajax/shared-claims/${this.claim.id}`;
            let method = shared ? 'delete' : 'post';

            axios({ method, url })
                .then(response => {
                    toastr[response.data.level](response.data.message, response.data.title, { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                })
                .catch(error => {
                    toastr['error'](error.response.data.message, 'ERROR', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                });            
        }
    },
    created() {

        const editableFields = ['patient_ssn', 'injury_date', 'injured_body_parts', 'employer_name', 'claim_number'];
        editableFields.forEach(key => {
            this.$set(this.edit, key, false);
        });

        window.Echo.private('claims.' + this.claim.id)
            .listen('ClaimShareEvent', (e) => {
                this.claim.is_shared = e.isShared;
            });
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }
}
</script>

<style scoped>
    .legend-sm {
        font-size: 1.2em;
        font-weight: bold;
    }
</style>